import { approvedVersionOf, isSameComponentType } from './hardwareInventory';

/*
 * Builds "the firmware versions this component type is running in the fleet" out of pages of
 * component rows.
 *
 * There is no endpoint that returns the distinct versions - the component type analytics row
 * carries only the oldest and the newest - so the set has to be counted from the component rows
 * themselves. Two things make that less simple than it sounds:
 *
 *   1. `manufacturer` and `model` are *partial* matches server-side and the rows carry no
 *      componentTypeId, so a query for one type comes back with its neighbours too. Every row is
 *      re-checked against the exact (category, manufacturer, model) triple before it is counted.
 *   2. `pagination.totalItems` counts that server-filtered superset, so it is only ever used for
 *      the paging arithmetic - never rendered as "N components of this type".
 *
 * Kept dependency-free: `moment` and `_` are webpack ProvidePlugin globals and are undefined when
 * mocha loads this module.
 */

export const EMPTY_VERSION_SCAN = {
	entries: [],
	keptCount: 0,
	unreportedCount: 0
};

const cloneEntries = (entries) => {
	return entries.map((entry) => {
		return {
			version: entry.version,
			componentCount: entry.componentCount,
			appliances: new Set(entry.appliances)
		};
	});
};

/*
 * Folds one page of component rows into the running scan. Returns a new scan rather than mutating,
 * so a page that arrives after its scan was superseded cannot corrupt the current one.
 */
export const mergeVersionScanPage = (scan, components, componentType) => {
	const current = scan ?? EMPTY_VERSION_SCAN;
	const entries = cloneEntries(current.entries);
	let keptCount = current.keptCount;
	let unreportedCount = current.unreportedCount;

	for (const component of components ?? []) {
		if (!isSameComponentType(component, componentType)) {
			continue;
		}

		keptCount += 1;

		/*
		 * A component that reports no firmware can never be matched by an advisory -
		 * `affectedVersions` is compared by exact string equality - so null is never an option.
		 * It is still counted, because those appliances being unreachable is something the author
		 * needs to know rather than something to hide.
		 */
		const version = typeof component?.firmwareVersion === 'string' ? component.firmwareVersion.trim() : '';

		if (!version) {
			unreportedCount += 1;
			continue;
		}

		const existing = entries.find((entry) => {
			return entry.version === version;
		});

		if (existing) {
			existing.componentCount += 1;
			existing.appliances.add(component.applianceSerialNumber);
			continue;
		}

		entries.push({
			version: version,
			componentCount: 1,
			appliances: new Set([component.applianceSerialNumber])
		});
	}

	return { entries: entries, keptCount: keptCount, unreportedCount: unreportedCount };
};

const catalogVersions = (componentType) => {
	return [
		{ version: componentType?.oldestFirmwareVersion, isOldest: true },
		{ version: componentType?.newestFirmwareVersion, isNewest: true },
		{ version: approvedVersionOf(componentType), isApproved: true }
	].filter((candidate) => {
		return Boolean(candidate.version);
	});
};

/*
 * Turns the scan into the option list.
 *
 * Ordered by how many components run the version, ties broken by the order the API returned them
 * in - which is documented as deterministic. Explicitly NOT alphabetical: sorting version strings
 * is how `10.0` ends up ranked below `9.0`. The API owns version ordering, and the only thing read
 * from it here is equality, so `oldestFirmwareVersion` / `newestFirmwareVersion` /
 * `approvedFirmwareVersion` are attached as labels rather than used as sort keys.
 *
 * The three catalog versions are unioned in at a zero count when the scan did not reach them: a
 * capped scan should not make a version that really is out there unofferable.
 */
export const collectFirmwareVersions = (scan, componentType) => {
	const current = scan ?? EMPTY_VERSION_SCAN;

	const seen = current.entries.map((entry) => {
		return {
			version: entry.version,
			componentCount: entry.componentCount,
			applianceCount: entry.appliances.size,
			seenInScan: true
		};
	});

	const missing = catalogVersions(componentType)
		.filter((candidate) => {
			return !seen.some((option) => {
				return option.version === candidate.version;
			});
		})
		.reduce((unique, candidate) => {
			if (unique.some((option) => {
				return option.version === candidate.version;
			})) {
				return unique;
			}

			return [...unique, { version: candidate.version, componentCount: 0, applianceCount: 0, seenInScan: false }];
		}, []);

	return [...seen, ...missing]
		.sort((left, right) => {
			return right.componentCount - left.componentCount;
		})
		.map((option) => {
			return {
				...option,
				isOldest: option.version === componentType?.oldestFirmwareVersion,
				isNewest: option.version === componentType?.newestFirmwareVersion,
				isApproved: option.version === approvedVersionOf(componentType)
			};
		});
};

/*
 * The paging arithmetic, kept next to the scan so the hook stays about orchestration.
 * `totalItems` is the superset count, which is exactly what the cap has to be measured against -
 * the walk stops on rows fetched, not on rows kept.
 */
export const hasNextScanPage = (fetchedCount, pageLength, pageLimit, totalItems) => {
	if (pageLength < pageLimit) {
		return false;
	}

	return fetchedCount < (totalItems ?? 0);
};
